import {
  FC,
  useState,
  useId,
  ReactNode,
  createContext,
  useContext,
} from 'react';
import { Menu, MenuProps } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { WbButton, WbButtonProps } from '../WbButton';

export type WbMenuButtonProps = Omit<WbButtonProps, 'onClick' | 'icon'> & {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
  menuProps?: Omit<MenuProps, 'anchorEl' | 'onClose' | 'open'>;
};

type WbMenuButtonContextValue = {
  closeMenu: () => void;
};

const WbMenuButtonContext = createContext<WbMenuButtonContextValue>({
  closeMenu: () => {},
});

export const useWbMenuButtonContext = () => useContext(WbMenuButtonContext);

export const WbMenuButton: FC<WbMenuButtonProps> = ({
  label,
  onClick,
  children,
  menuProps,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuId = useId();
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    onClick?.(e);
  };

  const handleClose = () => setAnchorEl(null);

  const composedLabel = (
    <>
      {label}

      <ArrowDropDownIcon fontSize="small" />
    </>
  );

  const sharedProps = {
    ...props,
    onClick: handleOpen,
    'aria-haspopup': 'true' as const,
    'aria-expanded': open,
    'aria-controls': menuId,
  };

  return (
    <>
      <WbButton {...sharedProps} label={composedLabel} />
      <Menu
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        {...menuProps}
        id={menuId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <WbMenuButtonContext.Provider value={{ closeMenu: handleClose }}>
          {children}
        </WbMenuButtonContext.Provider>
      </Menu>
    </>
  );
};
